<script setup lang="ts">
    import { inject, ref, watch } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import MissingValue from '@shared/components/MissingValue.vue';
    import ImagePreview from '@shared/components/Images/ImagePreview.vue';
    import RangeSliderNative from '@shared/components/RangeSliderNative.vue';
    import EventEditorDetailsNav from '@views/Event/EventEditor/Details/EventEditorDetailsNav.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { type Event } from '@views/Event/EventTypes';
    import { eventKey, queueEventPropertyKey, saveEventPropertyIfChangedKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;
    const saveEventPropertyIfChanged = inject(saveEventPropertyIfChangedKey)!;
    const queueEventProperty = inject(queueEventPropertyKey)!;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const useOrganizerInFooter = ref(false);
    const name = ref('');
    const email = ref('');
    const phone = ref('');
    const webUrl = ref('');
    const privacyUrl = ref('');
    const logoUrl = ref('');
    const logoWidth = ref(120);

    watch(
        () => event.value?.UseOrganizerInFooter,
        (value) => (useOrganizerInFooter.value = value ?? false),
        { immediate: true }
    );
    watch(
        () => event.value?.OrganizerName,
        (value) => (name.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.OrganizerEmail,
        (value) => (email.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.OrganizerPhone,
        (value) => (phone.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.OrganizerWebUrl,
        (value) => (webUrl.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.OrganizerPrivacyUrl,
        (value) => (privacyUrl.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.OrganizerLogoUrl,
        (value) => (logoUrl.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.OrganizerLogoWidth,
        (value) => (logoWidth.value = value ?? 120),
        { immediate: true }
    );

    function onUseOrganizerInFooterChange(newValue: boolean) {
        useOrganizerInFooter.value = newValue;
        saveEventProperty('UseOrganizerInFooter', String(newValue), $Resources.EditEventDetailsCustomOrganizerLabel);
    }

    function onLogoUrlChange(newValue: string) {
        logoUrl.value = newValue;
        saveEventProperty('OrganizerLogoUrl', newValue, $Resources.EventOrganizerLogoUrlLabel);
    }

    function onLogoWidthChange(newValue: number) {
        logoWidth.value = newValue;
        queueEventProperty('OrganizerLogoWidth', String(newValue), $Resources.AccountSettingsLogotypeWidth, 500);
    }

    function copyOrganizerInfoFromAccount() {
        const account = contextAccess.state.currentAccount;
        if (!account) return;

        name.value = account.PublicName;
        email.value = account.Email;
        phone.value = account.Phone;
        webUrl.value = account.WebUrl;
        privacyUrl.value = account.PrivacyUrl;

        saveEventProperty('OrganizerName', name.value, $Resources.EventOrganizerNameLabel);
        saveEventProperty('OrganizerEmail', email.value, $Resources.EventOrganizerEmailLabel);
        saveEventProperty('OrganizerPhone', phone.value, $Resources.EventOrganizerPhoneLabel);
        saveEventProperty('OrganizerWebUrl', webUrl.value, $Resources.EventOrganizerWebsiteLabel);
        saveEventProperty('OrganizerPrivacyUrl', privacyUrl.value, $Resources.EventOrganizerPolicyHeading);
        onLogoUrlChange(account.LogoUrl);
        onLogoWidthChange(account.LogoWidth);
    }
</script>

<template>
    <div v-if="event" class="max-900">
        <h2 class="h3 m-0-t">{{ $Resources.EditEventDetailsCustomOrganizerHeading }}</h2>
        <p v-html="$Resources.EditEventDetailsCustomOrganizerText"></p>

        <FeatureLock feature="Events.CustomOrganizer" :hideWhenLocked="false" showUpgradeAlert>
            <div class="form-group">
                <FormCheckable
                    :switchStyle="SwitchStyle.Standard"
                    :label="$Resources.EditEventDetailsCustomOrganizerLabel"
                    :checked="useOrganizerInFooter"
                    :disabled="!canEdit"
                    @update:modelValue="(newValue: boolean) => onUseOrganizerInFooterChange(newValue)" />

                <div v-if="useOrganizerInFooter" class="m-md-t">
                    <button type="button" class="btn btn-default btn-sm" :disabled="!canEdit" @click="copyOrganizerInfoFromAccount">
                        {{ $Resources.CopyOrganizerInfoFromAccountBtnLabel }}
                    </button>
                </div>
            </div>

            <div v-if="useOrganizerInFooter">
                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.EventOrganizerLogoUrlLabel }}:</span>
                    <ImagePreview
                        class="img-preview-no-mh"
                        :imageSrc="logoUrl"
                        :isDisabled="!canEdit"
                        :preview-size="{
                            width: logoWidth ? logoWidth : 200,
                        }"
                        @update:imageSrc="onLogoUrlChange"></ImagePreview>
                </div>

                <RangeSliderNative
                    class="form-group"
                    :class="{ 'pe-none gs-50 o-75': !logoUrl }"
                    :modelValue="`${logoWidth}px`"
                    :label="$Resources.AccountSettingsLogotypeWidth"
                    :min="40"
                    :max="600"
                    :step="10"
                    :disabled="!canEdit || !logoUrl"
                    suffix="px"
                    @update:modelValue="(newValue: string) => onLogoWidthChange(parseInt(newValue))"></RangeSliderNative>

                <div class="row">
                    <div class="col-md-4">
                        <FormInput
                            class="form-group"
                            :maxlength="255"
                            :label="$Resources.EventOrganizerNameLabel"
                            :disabled="!canEdit"
                            v-model="name"
                            @onBlur="saveEventPropertyIfChanged('OrganizerName', name, $Resources.EventOrganizerNameLabel)" />
                    </div>
                    <div class="col-md-4">
                        <FormInput
                            class="form-group"
                            :maxlength="100"
                            :label="$Resources.EventOrganizerEmailLabel"
                            :disabled="!canEdit"
                            v-model="email"
                            @onBlur="saveEventPropertyIfChanged('OrganizerEmail', email, $Resources.EventOrganizerEmailLabel)" />
                    </div>
                    <div class="col-md-4">
                        <FormInput
                            class="form-group"
                            :maxlength="20"
                            :label="$Resources.EventOrganizerPhoneLabel"
                            :disabled="!canEdit"
                            v-model="phone"
                            @onBlur="saveEventPropertyIfChanged('OrganizerPhone', phone, $Resources.EventOrganizerPhoneLabel)" />
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <FormInput
                            class="form-group"
                            type="url"
                            :maxlength="255"
                            :label="$Resources.EventOrganizerWebsiteLabel"
                            :disabled="!canEdit"
                            v-model="webUrl"
                            @onBlur="saveEventPropertyIfChanged('OrganizerWebUrl', webUrl, $Resources.EventOrganizerWebsiteLabel)" />
                    </div>
                    <div class="col-md-6">
                        <FormInput
                            class="form-group"
                            :maxlength="255"
                            :label="`${$Resources.EventOrganizerPolicyHeading} (url)`"
                            :disabled="!canEdit"
                            v-model="privacyUrl"
                            @onBlur="saveEventPropertyIfChanged('OrganizerPrivacyUrl', privacyUrl, $Resources.EventOrganizerPolicyHeading)" />
                    </div>
                </div>
            </div>
        </FeatureLock>

        <div v-if="!useOrganizerInFooter && contextAccess.state.currentAccount">
            <div class="m-lg-b">
                <span class="label-look d-block">{{ $Resources.EventOrganizerLogoUrlLabel }}:</span>
                <ImagePreview
                    class="img-preview-no-mh"
                    :imageSrc="contextAccess.state.currentAccount.LogoUrl"
                    :preview-size="{
                        width: contextAccess.state.currentAccount.LogoWidth || 200,
                    }"
                    isDisabled
                    readonly></ImagePreview>
            </div>

            <div class="row">
                <div class="col-md-4">
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.EventOrganizerNameLabel }}:</span>
                        <span v-if="contextAccess.state.currentAccount.PublicName">{{ contextAccess.state.currentAccount.PublicName }}</span>
                        <MissingValue v-else />
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.EventOrganizerEmailLabel }}:</span>
                        <span v-if="contextAccess.state.currentAccount.Email">{{ contextAccess.state.currentAccount.Email }}</span>
                        <MissingValue v-else />
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.EventOrganizerPhoneLabel }}:</span>
                        <span v-if="contextAccess.state.currentAccount.Phone">{{ contextAccess.state.currentAccount.Phone }}</span>
                        <MissingValue v-else />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-md-4">
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.EventOrganizerWebsiteLabel }}:</span>
                        <span v-if="contextAccess.state.currentAccount.WebUrl">{{ contextAccess.state.currentAccount.WebUrl }}</span>
                        <MissingValue v-else />
                    </div>
                </div>
                <div class="col-md-8">
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.EventOrganizerPolicyHeading }} (url):</span>
                        <span v-if="contextAccess.state.currentAccount.PrivacyUrl">{{ contextAccess.state.currentAccount.PrivacyUrl }}</span>
                        <MissingValue v-else />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <EventEditorDetailsNav v-if="event" :eventId="props.eventId" />
</template>
